import React, { useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {
  playSound, isSoundMuted, toggleSoundMute, setSoundProfile,
  getSoundProfile, SOUND_PROFILES, playCrtDegauss, playFlybackPulse
} from '../../lib/soundEffects';

const { FiVolume2, FiVolumeX, FiRadio, FiZap, FiMusic, FiTv } = FiIcons;

const ICONS = {
  cyberpunk: FiZap,
  arcade: FiMusic,
  quantum: FiRadio,
  stealth: FiRadio,
  crt: FiTv,
};

export default function TabAudio({ theme, accent = '#10b981' }) {
  const { textColor, mutedText, inputBg, inputBorder, surfaceBg, cardBg } = theme;
  const [muted, setMuted] = useState(isSoundMuted());
  const [volume, setVolume] = useState(() => {
    return parseFloat(localStorage.getItem('memark_sound_volume') || '0.15');
  });
  const [selectedProfile, setSelectedProfile] = useState(() => getSoundProfile());

  const handleToggleMute = () => {
    const next = toggleSoundMute();
    setMuted(next);
    if (!next) playSound('click');
  };

  const handleProfileSelect = (id) => {
    setSelectedProfile(id);
    setSoundProfile(id);
    playSound('launch');
  };

  const handleVolumeChange = (e) => {
    const v = parseFloat(e.target.value);
    setVolume(v);
    localStorage.setItem('memark_sound_volume', v.toString());
  };

  return (
    <div className="space-y-6">
      {/* Master Toggle */}
      <div className="flex items-center justify-between p-4 rounded-2xl border"
        style={{ backgroundColor: surfaceBg, borderColor: inputBorder }}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${accent}20`, border: `1px solid ${accent}40` }}>
            <SafeIcon icon={muted ? FiVolumeX : FiVolume2} className="w-5 h-5" style={{ color: accent }} />
          </div>
          <div>
            <p className="text-sm font-semibold" style={{ color: textColor }}>
              Procedural Web Audio Engine
            </p>
            <p className="text-xs" style={{ color: mutedText }}>
              Synthesized in-browser sound effects with 0 network latency
            </p>
          </div>
        </div>

        <button
          onClick={handleToggleMute}
          className="px-4 py-2 rounded-xl text-xs font-mono font-semibold transition-all shadow-sm"
          style={{
            backgroundColor: muted ? 'rgba(239,68,68,0.15)' : `${accent}25`,
            color: muted ? '#f87171' : accent,
            border: `1px solid ${muted ? 'rgba(239,68,68,0.3)' : `${accent}50`}`,
          }}
        >
          {muted ? 'MUTED (OFF)' : 'ACTIVE (ON)'}
        </button>
      </div>

      {/* Volume Slider */}
      <div className="space-y-2">
        <div className="flex justify-between items-center text-xs">
          <span className="font-semibold" style={{ color: textColor }}>Master Synth Gain</span>
          <span className="font-mono text-[11px]" style={{ color: accent }}>{Math.round(volume * 100)}%</span>
        </div>
        <input
          type="range"
          min="0.02"
          max="0.4"
          step="0.01"
          value={volume}
          onChange={handleVolumeChange}
          disabled={muted}
          className="w-full accent-emerald-400 cursor-pointer"
        />
      </div>

      {/* 4 Soundpack Profiles Selector */}
      <div className="space-y-3">
        <p className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: mutedText }}>
          4 Procedural Sound Themes
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {SOUND_PROFILES.map((p) => {
            const isSelected = selectedProfile === p.id;
            const IconComponent = ICONS[p.id] || FiZap;
            return (
              <button
                key={p.id}
                onClick={() => handleProfileSelect(p.id)}
                disabled={muted}
                className="p-3.5 rounded-xl border text-left transition-all relative overflow-hidden"
                style={{
                  backgroundColor: isSelected ? `${accent}15` : cardBg,
                  borderColor: isSelected ? accent : inputBorder,
                  opacity: muted ? 0.5 : 1,
                }}
              >
                {isSelected && (
                  <div className="absolute top-2 right-2 w-2 h-2 rounded-full shadow-[0_0_8px_#10b981]" style={{ backgroundColor: accent }} />
                )}
                <SafeIcon icon={IconComponent} className="w-4 h-4 mb-2" style={{ color: isSelected ? accent : mutedText }} />
                <p className="text-xs font-bold mb-1" style={{ color: isSelected ? textColor : mutedText }}>{p.name}</p>
                <p className="text-[10.5px] leading-relaxed" style={{ color: mutedText }}>{p.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Sound Test Lab with Live Equalizer Visualizer */}
      <div className="space-y-3 pt-2 border-t" style={{ borderColor: inputBorder }}>
        <div className="flex items-center justify-between">
          <p className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: mutedText }}>
            Interactive Sound Test Lab &amp; Equalizer Waveform
          </p>
          <span className="text-[9px] font-mono text-emerald-400">Web Audio API • 48kHz</span>
        </div>

        {/* Live Synthesizer Waveform Visualizer */}
        <div className="h-14 rounded-xl bg-black/40 border border-[#222] p-2 flex items-end justify-between gap-1 overflow-hidden">
          {Array.from({ length: 24 }).map((_, i) => {
            const h = muted ? 4 : Math.max(8, ((i * 17) % 40) + 10);
            return (
              <div
                key={i}
                className="flex-1 rounded-t-sm transition-all duration-300"
                style={{
                  height: `${h}px`,
                  backgroundColor: muted ? '#333' : i % 2 === 0 ? accent : '#a78bfa',
                  opacity: muted ? 0.3 : 0.85,
                }}
              />
            );
          })}
        </div>

        <div className="flex flex-wrap gap-2">
          {['click', 'launch', 'lock', 'copy', 'success'].map((type) => (
            <button
              key={type}
              onClick={() => playSound(type)}
              disabled={muted}
              className="px-3 py-1.5 rounded-lg border text-xs font-mono capitalize transition-all hover:scale-105 active:scale-95"
              style={{
                backgroundColor: surfaceBg,
                borderColor: inputBorder,
                color: textColor,
              }}
            >
              ▶ Test {type}
            </button>
          ))}
          <button
            onClick={() => playCrtDegauss()}
            disabled={muted}
            className="px-3 py-1.5 rounded-lg border text-xs font-mono transition-all hover:scale-105 active:scale-95 bg-amber-500/15 border-amber-500/40 text-amber-300"
          >
            ⚡ CRT Degauss
          </button>
          <button
            onClick={() => playFlybackPulse()}
            disabled={muted}
            className="px-3 py-1.5 rounded-lg border text-xs font-mono transition-all hover:scale-105 active:scale-95 bg-sky-500/15 border-sky-500/40 text-sky-300"
          >
            ⚡ CRT Flyback
          </button>
        </div>
      </div>
    </div>
  );
}
